import { z } from "zod";

// Global phone validation - accepts any format with minimum digits
const validateGlobalPhone = (phone: string): boolean => {
  // Remove all non-digit characters except + at the start
  const cleaned = phone.replace(/[^\d+]/g, "");
  const digits = cleaned.replace(/\D/g, "");

  // Must have at least 7 digits (shortest valid phone numbers)
  // and no more than 15 (E.164 standard max)
  return digits.length >= 7 && digits.length <= 15;
};

// Experience level options
export const experienceOptions = [
  { value: "0-1", label: "Less than 1 year" },
  { value: "1-3", label: "1-3 years" },
  { value: "3-5", label: "3-5 years" },
  { value: "5-10", label: "5-10 years" },
  { value: "10+", label: "10+ years" },
] as const;

// Notice period options
export const noticePeriodOptions = [
  { value: "Immediate", label: "Immediate" },
  { value: "2 Weeks", label: "2 Weeks" },
  { value: "1 Month", label: "1 Month" },
  { value: "2 Months", label: "2 Months" },
  { value: "More than 2 Months", label: "More than 2 Months" },
] as const;

// Application form schema
export const applicationFormSchema = z.object({
  fullName: z
    .string()
    .min(2, "Full name must be at least 2 characters")
    .max(100, "Full name can be at most 100 characters")
    .regex(
      /^[A-Za-z\s'-]+$/,
      "Full name must contain only letters, spaces, hyphens, and apostrophes"
    ),

  email: z.string().email("Please enter a valid email address"),

  phone: z
    .string()
    .min(1, "Phone number is required")
    .refine(validateGlobalPhone, "Please enter a valid phone number (7-15 digits)"),

  yearsOfExperience: z.string().min(1, "Please select your years of experience"),

  relevantTools: z
    .string()
    .min(10, "Please provide at least 10 characters describing your tools and skills")
    .max(2000, "Tools description can be at most 2000 characters"),

  expectedSalary: z
    .string()
    .optional()
    .refine(
      (val) => !val || val.length >= 1,
      "Please enter your expected salary"
    ),

  noticePeriod: z
    .string()
    .optional()
    .refine(
      (val) => !val || val.length >= 1,
      "Please select your notice period"
    ),

  privacyConsent: z
    .boolean()
    .refine((val) => val === true, "You must agree to the Privacy Policy to submit your application"),
});

export type ApplicationFormSchemaData = z.infer<typeof applicationFormSchema>;
